import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const WORDS_LINE1 = ['Du', 'gibst', 'alles.'];
const WORDS_LINE2 = ['Und', 'trotzdem', 'reicht', 'es', 'nie.'];

const GlowWord = ({ word, delay, color = 'cream' }) => (
  <motion.span
    initial={{ opacity: 0, y: 60, filter: 'blur(20px)' }}
    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
    transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    className="inline-block mr-4"
    style={{ color: color === 'gold' ? '#D4AF37' : '#F9F9F9' }}
  >
    {word}
  </motion.span>
);

const HeroSection = () => {
  const ref = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const handleMouseMove = (e) => {
    setMousePos({
      x: (e.clientX / window.innerWidth) * 100,
      y: (e.clientY / window.innerHeight) * 100,
    });
  };

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* BG Layers */}
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <div className="absolute inset-0 bg-dark" />
        <div
          className="absolute inset-0 transition-all duration-500 pointer-events-none"
          style={{
            background: `radial-gradient(900px circle at ${mousePos.x}% ${mousePos.y}%, rgba(212,175,55,0.07) 0%, transparent 60%)`,
          }}
        />
        <motion.div
          animate={{ opacity: [0.3, 0.7, 0.3], scale: [1, 1.08, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.08) 0%, transparent 60%)' }}
        />
        <motion.div
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 13, repeat: Infinity }}
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 20% 75%, rgba(114,47,55,0.15) 0%, transparent 50%)' }}
        />
        {/* Gold threads */}
        {[...Array(7)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute top-0 bottom-0 w-[1px]"
            style={{
              left: `${12 + i * 13}%`,
              background: i % 2 === 0
                ? 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.08), transparent)'
                : 'linear-gradient(to bottom, transparent, rgba(114,47,55,0.06), transparent)',
              filter: 'blur(1px)',
            }}
            animate={{ opacity: [0, 0.9, 0], scaleY: [0.4, 1, 0.4] }}
            transition={{ duration: 5 + i * 1.3, repeat: Infinity, delay: i * 0.9 }}
          />
        ))}
        {/* Particles */}
        {[...Array(16)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gold pointer-events-none"
            style={{
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{ y: [-25, 25, -25], opacity: [0.05, 0.45, 0.05] }}
            transition={{ duration: 5 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 4 }}
          />
        ))}
        {/* Film grain */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: '200px',
          }}
        />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-32">
        <motion.p
          initial={{ opacity: 0, letterSpacing: '0.05em' }}
          animate={{ opacity: 1, letterSpacing: '0.4em' }}
          transition={{ duration: 2, delay: 0.3 }}
          className="text-gold text-xs uppercase font-semibold mb-10"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.8)' }}
        >
          Die Heilung der weiblichen Beziehungs-Matrix
        </motion.p>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif leading-tight mb-8">
          <div className="flex flex-wrap justify-center mb-3">
            {WORDS_LINE1.map((w, i) => (
              <GlowWord key={i} word={w} delay={0.6 + i * 0.15} />
            ))}
          </div>
          <div className="flex flex-wrap justify-center">
            {WORDS_LINE2.map((w, i) => (
              <GlowWord key={i} word={w} delay={1.2 + i * 0.13} color={i === 4 ? 'gold' : 'cream'} />
            ))}
          </div>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 2.2 }}
          className="text-xl md:text-2xl font-serif italic text-cream/75 max-w-3xl mx-auto mb-6 leading-relaxed"
        >
          Das Problem ist nicht, dass du die Falschen anziehst.{' '}
          <span className="text-wine-light" style={{ textShadow: '0 0 15px rgba(155,66,78,0.4)' }}>
            Das Problem ist, dass dein System gelernt hat, Schmerz mit Liebe zu verwechseln.
          </span>
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 3 }}
          className="text-lg text-cream/55 max-w-2xl mx-auto mb-14 font-light leading-relaxed"
        >
          Du analysierst jede Nachricht. Du wartest. Du gibst. Du biegst dich. Du verlierst dich.
          Und irgendwo, tief in deiner Brust, weißt du bereits: So soll Liebe nicht sein.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 3.5 }}
        >
          <motion.a
            href="#spiegel"
            whileHover={{ scale: 1.06, boxShadow: '0 0 70px rgba(212,175,55,0.8), 0 0 140px rgba(212,175,55,0.4)' }}
            whileTap={{ scale: 0.97 }}
            className="inline-block px-14 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer relative overflow-hidden group"
            style={{ boxShadow: '0 0 35px rgba(212,175,55,0.5)', borderRadius: '2px' }}
          >
            <span className="relative z-10">✦ Ich bin bereit für meine Heilung</span>
            <div className="absolute inset-0 bg-white/15 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
          </motion.a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4.2 }}
          className="text-xs text-cream/25 mt-6 tracking-widest uppercase"
        >
          Kostenfreies Erstgespräch · Begrenzte Plätze
        </motion.p>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 14, 0], opacity: [0.2, 1, 0.2] }}
        transition={{ repeat: Infinity, duration: 3.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <div className="w-1 h-1 rounded-full bg-gold" style={{ boxShadow: '0 0 12px rgba(212,175,55,1)' }} />
        <div className="w-[1px] h-16 bg-gradient-to-b from-gold via-gold/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default HeroSection;